import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useLaunchConfig, useLaunchForecast } from '@/hooks/useLaunch';
import { LoadingPage, LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorPage, ErrorMessage } from '@/components/ui/ErrorMessage';
import { ExplainabilityCard, generateLaunchExplanation } from '@/components/ui/ExplainabilityCard';

function formatCurrency(value: number): string {
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

function formatVolume(value: number): string {
  if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M units`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(0)}K units`;
  return `${value.toFixed(0)} units`;
}

function getVerdictStyle(verdict: string): { bg: string; text: string } {
  switch (verdict) {
    case 'go': return { bg: 'verdict-go', text: 'GO' };
    case 'refine': return { bg: 'verdict-refine', text: 'REFINE' };
    case 'pause': return { bg: 'verdict-pause', text: 'PAUSE' };
    default: return { bg: 'bg-surface-600', text: verdict.toUpperCase() };
  }
}

function getCannibalizationColor(risk: string): string {
  switch (risk) {
    case 'low': return 'text-success-400';
    case 'medium': return 'text-warning-400';
    case 'high': return 'text-danger-400';
    default: return 'text-text-secondary';
  }
}

interface LaunchFormData {
  product_name: string;
  price: number;
  trial_rate: number;
  repeat_rate: number;
  distribution_month3: number;
  distribution_month6: number;
  distribution_month12: number;
}

export function ClientLaunchPage() {
  const { clientId } = useParams();
  const configQuery = useLaunchConfig(clientId);
  const forecastMutation = useLaunchForecast(clientId);

  const [formData, setFormData] = useState<LaunchFormData>({
    product_name: 'New Product',
    price: 2.99,
    trial_rate: 0.15,
    repeat_rate: 0.35,
    distribution_month3: 0.3,
    distribution_month6: 0.5,
    distribution_month12: 0.7,
  });

  // Update form with default assumptions when config loads
  const config = configQuery.data;

  if (configQuery.isLoading) {
    return <LoadingPage message="Loading launch configuration..." />;
  }

  if (configQuery.error) {
    return <ErrorPage error={configQuery.error} onRetry={() => configQuery.refetch()} />;
  }

  const handleInputChange = (field: keyof LaunchFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: field === 'product_name' ? value : parseFloat(value) || 0,
    }));
  };

  const handleForecast = () => {
    forecastMutation.mutate({
      product: {
        name: formData.product_name,
        price: formData.price,
      },
      assumptions: {
        trial_rate: formData.trial_rate,
        repeat_rate: formData.repeat_rate,
        distribution_month3: formData.distribution_month3,
        distribution_month6: formData.distribution_month6,
        distribution_month12: formData.distribution_month12,
      },
    });
  };

  const forecast = forecastMutation.data;
  const verdictStyle = forecast ? getVerdictStyle(forecast.verdict) : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-white mb-6">Launch Forecasting</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Input Form */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Product Details</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Product Name</label>
              <input
                type="text"
                value={formData.product_name}
                onChange={(e) => handleInputChange('product_name', e.target.value)}
                className="w-full px-3 py-2 bg-surface-600 border border-surface-400/30 rounded text-white focus:outline-none focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-1">Price ($)</label>
              <input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => handleInputChange('price', e.target.value)}
                className="w-full px-3 py-2 bg-surface-600 border border-surface-400/30 rounded text-white focus:outline-none focus:border-primary-500"
              />
            </div>

            <div className="pt-2 border-t border-surface-400/30">
              <h4 className="text-sm font-medium text-white mb-3">Assumptions</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-text-secondary mb-1">Trial Rate</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={formData.trial_rate}
                    onChange={(e) => handleInputChange('trial_rate', e.target.value)}
                    className="w-full px-3 py-2 bg-surface-600 border border-surface-400/30 rounded text-white text-sm focus:outline-none focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-text-secondary mb-1">Repeat Rate</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={formData.repeat_rate}
                    onChange={(e) => handleInputChange('repeat_rate', e.target.value)}
                    className="w-full px-3 py-2 bg-surface-600 border border-surface-400/30 rounded text-white text-sm focus:outline-none focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-text-secondary mb-1">Distribution M3 / M6 / M12</label>
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={formData.distribution_month3}
                      onChange={(e) => handleInputChange('distribution_month3', e.target.value)}
                      className="px-2 py-2 bg-surface-600 border border-surface-400/30 rounded text-white text-sm focus:outline-none focus:border-primary-500"
                    />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={formData.distribution_month6}
                      onChange={(e) => handleInputChange('distribution_month6', e.target.value)}
                      className="px-2 py-2 bg-surface-600 border border-surface-400/30 rounded text-white text-sm focus:outline-none focus:border-primary-500"
                    />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={formData.distribution_month12}
                      onChange={(e) => handleInputChange('distribution_month12', e.target.value)}
                      className="px-2 py-2 bg-surface-600 border border-surface-400/30 rounded text-white text-sm focus:outline-none focus:border-primary-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={handleForecast}
              disabled={forecastMutation.isPending}
              className="w-full mt-4 px-4 py-2 bg-primary-500 text-white rounded hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {forecastMutation.isPending ? (
                <>
                  <LoadingSpinner size="sm" />
                  Forecasting...
                </>
              ) : (
                'Run Forecast'
              )}
            </button>

            {forecastMutation.error && (
              <ErrorMessage error={forecastMutation.error} />
            )}
          </div>
        </div>

        {/* Results */}
        <div className="lg:col-span-2 space-y-6">
          {/* Verdict Card */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Verdict</h3>
            {forecast ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className={`p-6 ${verdictStyle?.bg} rounded-lg text-center`}>
                    <div className="text-3xl font-bold">{verdictStyle?.text}</div>
                    <div className="text-sm mt-2">{(forecast.confidence * 100).toFixed(0)}% Confidence</div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between p-2 bg-surface-600 rounded">
                      <span className="text-text-secondary">Year 1 Volume</span>
                      <span className="text-white font-medium">{formatVolume(forecast.year1_volume)}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-surface-600 rounded">
                      <span className="text-text-secondary">Year 1 Revenue</span>
                      <span className="text-white font-medium">{formatCurrency(forecast.year1_revenue)}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-surface-600 rounded">
                      <span className="text-text-secondary">Market Share</span>
                      <span className="text-white font-medium">{(forecast.market_share * 100).toFixed(2)}%</span>
                    </div>
                    <div className="flex justify-between p-2 bg-surface-600 rounded">
                      <span className="text-text-secondary">Cannibalization Risk</span>
                      <span className={`font-medium capitalize ${getCannibalizationColor(forecast.cannibalization_risk)}`}>
                        {forecast.cannibalization_risk}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Explainability Card - Why this verdict? */}
                <ExplainabilityCard
                  {...generateLaunchExplanation(forecast.verdict as 'go' | 'refine' | 'pause', {
                    netIncremental: (forecast.source_of_volume.market_growth +
                                    forecast.source_of_volume.competitor +
                                    forecast.source_of_volume.new_users),
                    cannibalizationRisk: forecast.source_of_volume.own_portfolio,
                    marketShare: forecast.market_share * 100,
                    trialRate: formData.trial_rate,
                    repeatRate: formData.repeat_rate,
                    highRiskSKUs: forecast.cannibalization_risk === 'high' ? 2 :
                                  forecast.cannibalization_risk === 'medium' ? 1 : 0,
                  })}
                />
              </div>
            ) : (
              <div className="h-40 flex items-center justify-center border border-dashed border-surface-400/30 rounded-lg">
                <span className="text-text-tertiary">Run a forecast to see results</span>
              </div>
            )}
          </div>

          {/* Source of Volume */}
          {forecast && (
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Source of Volume</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="p-4 bg-surface-600 rounded-lg">
                  <div className="text-2xl font-bold text-success-400">
                    {(forecast.source_of_volume.market_growth * 100).toFixed(0)}%
                  </div>
                  <div className="text-sm text-text-secondary mt-1">Market Growth</div>
                </div>
                <div className="p-4 bg-surface-600 rounded-lg">
                  <div className="text-2xl font-bold text-primary-400">
                    {(forecast.source_of_volume.competitor * 100).toFixed(0)}%
                  </div>
                  <div className="text-sm text-text-secondary mt-1">Competitor</div>
                </div>
                <div className="p-4 bg-surface-600 rounded-lg">
                  <div className="text-2xl font-bold text-accent-purple">
                    {(forecast.source_of_volume.new_users * 100).toFixed(0)}%
                  </div>
                  <div className="text-sm text-text-secondary mt-1">New Users</div>
                </div>
                <div className="p-4 bg-surface-600 rounded-lg">
                  <div className="text-2xl font-bold text-warning-400">
                    {(forecast.source_of_volume.own_portfolio * 100).toFixed(0)}%
                  </div>
                  <div className="text-sm text-text-secondary mt-1">Own Portfolio</div>
                </div>
              </div>
            </div>
          )}

          {/* Config Info */}
          {config && (
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Market Context</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-text-secondary">GPC Brick:</span>
                  <span className="text-white ml-2">{config.market.gpc_brick}</span>
                </div>
                <div>
                  <span className="text-text-secondary">Country:</span>
                  <span className="text-white ml-2">{config.market.country}</span>
                </div>
              </div>
              {config.default_assumptions && (
                <div className="mt-4 pt-4 border-t border-surface-400/30">
                  <div className="text-sm text-text-secondary mb-2">Default Assumptions</div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="p-2 bg-surface-600 rounded">
                      <div className="text-text-secondary">Trial</div>
                      <div className="text-white">{(config.default_assumptions.trial_rate * 100).toFixed(0)}%</div>
                    </div>
                    <div className="p-2 bg-surface-600 rounded">
                      <div className="text-text-secondary">Repeat</div>
                      <div className="text-white">{(config.default_assumptions.repeat_rate * 100).toFixed(0)}%</div>
                    </div>
                    <div className="p-2 bg-surface-600 rounded">
                      <div className="text-text-secondary">Dist M12</div>
                      <div className="text-white">{(config.default_assumptions.distribution_month12 * 100).toFixed(0)}%</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
